/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useCallback, useState } from 'react';

import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  MenuItem,
  useCheckboxGroup,
  Tag,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  Center,
  Spinner,
  Text,
  MenuButtonProps,
  Portal,
  Input,
} from '@chakra-ui/core';
import isString from 'lodash/isString';
import { MdOutlineRadioButtonUnchecked as Radio, MdRadioButtonChecked as RadioSelected } from 'react-icons/md';

// Type for options
export type Option = string | { value: string; label: string };

// Props
type SelectMenuProps = {
  label: React.ReactNode; // Default button text
  selectAllLabel?: string;
  options?: Option[]; // Static options
  loadOptions?: () => Promise<Option[]>; // Async loader (called on open)
  variant?: 'checkbox' | 'plain' | 'radio'; // Default: "checkbox"
  selected?: string[]; // Controlled selected values
  onChange?: (values: string[]) => void; // For checkbox mode
  onSelectItem?: (value: string) => void; // For plain mode
  maxHeight?: number; // Scroll max height (default: 400)
  withSearch?: boolean;
  withSelectAll?: boolean;
  isDisabled?: boolean; // Disable menu
  hideDownIcon?: boolean;
  menuBottonProps?: MenuButtonProps;
  requireSelection?: boolean;
};

const OptionRow = React.memo(function OptionRow({
  opt,
  isCheckbox,
  getCheckboxProps,
  onSelectItem,
  isSelected,
  variant,
  isLastSelected,
}: {
  opt: { value: string; label: string };
  isCheckbox: boolean;
  getCheckboxProps: (props: { value: string }) => any;
  onSelectItem?: (value: string) => void;
  isSelected: boolean;
  variant: 'checkbox' | 'plain' | 'radio';
  isLastSelected: boolean;
}) {
  if (isCheckbox) {
    return (
      <Box>
        <Checkbox px={4} py={2} w="100%" isDisabled={isLastSelected} {...getCheckboxProps({ value: opt.value })}>
          {opt.label}
        </Checkbox>
      </Box>
    );
  }
  if (variant === 'radio') {
    return (
      <MenuItem
        onClick={() => {
          onSelectItem?.(opt.value);
        }}
        px={4}
        py={2}
      >
        <HStack w="100%">
          {isSelected ? (
            <RadioSelected data-testid={`radio-selected-${opt.value}`} />
          ) : (
            <Radio data-testid={`radio-${opt.value}`} />
          )}
          <Text>{opt.label}</Text>
        </HStack>
      </MenuItem>
    );
  }
  return (
    <MenuItem onClick={() => onSelectItem?.(opt.value)} px={4} py={2}>
      {opt.label}
    </MenuItem>
  );
});

export function SelectMenu({
  label,
  selectAllLabel,
  options,
  loadOptions,
  variant = 'checkbox',
  selected = [],
  onChange,
  onSelectItem,
  maxHeight = 400,
  withSearch = false,
  withSelectAll = false,
  isDisabled = false,
  hideDownIcon = false,
  menuBottonProps,
  requireSelection = false,
}: SelectMenuProps) {
  const [asyncOptions, setAsyncOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Final options (prefer async over static if provided)
  const finalOptions = useMemo<Option[]>(() => (loadOptions ? asyncOptions : options || []), [
    loadOptions,
    asyncOptions,
    options,
  ]);

  const normalizedOptions = useMemo(() => finalOptions.map((opt) => (isString(opt) ? { value: opt, label: opt } : opt)), [
    finalOptions,
  ]);

  const { value, setValue, getCheckboxProps } = useCheckboxGroup({
    value: selected,
    onChange,
  });
  const isAllSelected = value?.length === normalizedOptions.length && normalizedOptions.length > 0;
  const isIndeterminate = value?.length > 0 && value.length < normalizedOptions.length;

  const handleSelectAll = useCallback(() => {
    setValue(isAllSelected ? [] : normalizedOptions.map((opt) => opt.value));
  }, [isAllSelected, normalizedOptions, setValue]);

  useEffect(() => {
    setValue(selected);
  }, [selected, setValue]);

  // Button content with chip display
  const buttonContent = useMemo(() => {
    if (variant !== 'checkbox' || value.length === 0) {
      if (isString(label))
        return (
          <Text fontSize="sm" whiteSpace="nowrap" maxW={100} isTruncated>
            {label}
          </Text>
        );
      return label;
    }
    if (isAllSelected) {
      return (
        <HStack>
          <Text fontSize="sm">{selectAllLabel}</Text>
          {options?.length && <Text fontSize="xs">{`(${options?.length})`}</Text>}
        </HStack>
      );
    }

    const selectedOptions = normalizedOptions.filter((opt) => value.includes(opt.value));
    if (value.length === 1) {
      return <Tag colorScheme="blue">{selectedOptions[0].label}</Tag>;
    }
    return (
      <HStack spacing={2}>
        <Tag colorScheme="blue">{selectedOptions[0].label}</Tag>
        <Text fontSize="sm" color="gray.600">
          {`+${value.length - 1}`}
        </Text>
      </HStack>
    );
  }, [variant, value, isAllSelected, normalizedOptions, label, selectAllLabel, options]);

  // Load async data only when menu opens
  const handleMenuOpen = useCallback(async () => {
    if (!loadOptions || hasFetched) return;
    setLoading(true);
    try {
      const data = await loadOptions();
      setAsyncOptions(data);
      setHasFetched(true);
    } catch (err) {
      console.error('Failed to load options:', err);
    } finally {
      setLoading(false);
    }
  }, [loadOptions, hasFetched]);

  return (
    <Menu closeOnSelect={variant !== 'checkbox'} isLazy onOpen={handleMenuOpen} gutter={2} matchWidth>
      <MenuButton
        px={2}
        py={1}
        transition="all 0.2s"
        borderRadius="md"
        borderWidth="1px"
        _focus={{ boxShadow: 'outline' }}
        flexGrow={1}
        disabled={isDisabled}
        data-testid="select-button"
        {...menuBottonProps}
      >
        <HStack justify={hideDownIcon ? 'center' : 'space-between'}>
          {buttonContent}
          {!hideDownIcon && <ChevronDownIcon />}
        </HStack>
      </MenuButton>
      <Portal>
        <MenuList maxHeight={maxHeight} maxWidth="fit-content" overflowY="auto" p={0}>
          {/* Loader */}
          {loading && (
            <Center py={4}>
              <Spinner size="sm" />
            </Center>
          )}

          {/* Sticky Select All */}
          {!loading && variant === 'checkbox' && normalizedOptions.length > 0 && (
            <Box position="sticky" top={0} bg="white" zIndex={1}>
              {withSelectAll && (
                <Checkbox
                  px={4}
                  py={2}
                  w="100%"
                  isChecked={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                >
                  <Text fontSize="sm" lineHeight={1}>
                    {selectAllLabel || 'Select All'}
                  </Text>
                </Checkbox>
              )}

              {withSearch && (
                <Input
                  placeholder="Search..."
                  borderLeftColor="transparent"
                  borderRight="transparent"
                  _focus={{
                    outline: '2px solid blue',
                    outlineOffset: '-2px',
                  }}
                  borderRadius={0}
                />
              )}
            </Box>
          )}

          {/* Options */}
          {!loading &&
            normalizedOptions.map((opt) => (
              <OptionRow
                key={opt.value}
                opt={opt}
                isCheckbox={variant === 'checkbox'}
                getCheckboxProps={getCheckboxProps}
                onSelectItem={onSelectItem}
                isSelected={value.includes(opt.value)}
                variant={variant}
                isLastSelected={requireSelection && value.length === 1 && value.includes(opt.value)}
              />
            ))}

          {/* Empty state */}
          {!loading && normalizedOptions.length === 0 && (
            <Center py={3}>
              <Text fontSize="sm" color="gray.500">
                No options available
              </Text>
            </Center>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}
